
import Vue from "vue";
import { mapState } from "vuex";
import { IRootState } from "@/store/types";
import { ReferenceDataType } from "@/store/referenceData/types";
import { REFERENCE_DATA } from "@/store/referenceData";
import { PROMO_CODES } from "@/store/promoCodes";
import { IPromoCodeDto } from "@/store/promoCodes/types";
import {
  alphaNum,
  minLength,
  maxValue,
  maxLength,
  required,
  requiredIf,
  decimal,
  integer,
  numeric,
  or
} from "vuelidate/lib/validators";
import { date, dateGreaterThan } from "@/validators";
import { addYears, addDays } from "date-fns";
import { setTimeout, clearTimeout } from "timers";
import { ROUTE_NAMES } from "@/routes";
import { isInSomeRole } from "@/helpers/auth";
import { SUPPORT, PRICING_TEAM } from "@/constants/roles";

const MAX_DATE_VALUE_YEARS = 5;
const EDITOR_MAX_LENGTH = 1200;

export default Vue.extend({
  props: {
    id: {
      // Comes through as a number from the router
      // And a string if you hit the URL directly
      type: [Number, String],
      required: false
    }
  },
  data() {
    return {
      EDITOR_MAX_LENGTH,
      ReferenceDataType,
      legalLength: 0,
      benefitsLength: 0,
      listRouteName: ROUTE_NAMES.PROMO_CODES,
      originalValidFromDate: null
    };
  },
  watch: {
    id: {
      immediate: true,
      handler(value: string) {
        this.$store.dispatch(PROMO_CODES.NS(PROMO_CODES.ACTIONS.RESET_STATE));
        this.fetchData();
      }
    }
  },
  computed: {
    hasEditAccess() {
      return isInSomeRole([PRICING_TEAM, SUPPORT]);
    },
    isDisabled() {
      return !this.hasEditAccess || this.isLoading || this.uiIsLoading;
    },
    ...mapState({
      isLoading: (state: IRootState) => state.promoOffer.isLoading,
      uiIsLoading: (state: IRootState) => state.ui.isLoading
    }),
    isPromoCodeEditable(): boolean {
      return !this.id || this.id === "";
    },
    deliveryMethod: {
      get(): string {
        return this.model.deliveryMethod;
      },
      set(value: string) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_DELIVERY_METHOD),
          value
        );
      }
    },
    minFromDateValue(): Date | undefined {
      const now = new Date();

      // Existing promos can use the existing date or todays date, whichever is earlier.
      if (this.id && this.originalValidFromDate) {
        return new Date(this.originalValidFromDate) > now
          ? now
          : this.originalValidFromDate;
      }

      // New promo offers should not be dated in the past
      return now;
    },
    minToDateValue(): Date | undefined {
      let minDate = new Date();

      // The min to date should be after the from date
      if (this.model && this.model.validFromDate) {
        minDate = this.model.validFromDate;
      }

      return addDays(minDate, 1);
    },
    maxDateValue(): Date {
      return addYears(new Date(), MAX_DATE_VALUE_YEARS);
    },
    model(): IPromoCodeDto {
      return this.$store.state.promoOffer.promoCode;
    },
    promoCode: {
      get(): string {
        return this.model.promoCode;
      },
      set(value: string) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_CODE),
          value
        );
      }
    },
    singleUse: {
      get(): boolean {
        return this.model.singleUse;
      },
      set(value: boolean) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_SINGLE_USE),
          value
        );
      }
    },
    membershipNoRequired: {
      get(): boolean {
        return this.model.membershipNoRequired;
      },
      set(value: boolean) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_MEMBERSHIP_NO_REQUIRED),
          value
        );
      }
    },
    validFromDate: {
      get(): any {
        return this.model.validFromDate;
      },
      set(value: Date) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_VALID_FROM_DATE),
          value
        );

        // Re-validate validToDate in case it is now valid and vuelidate hasn't updated
        // the error state. This can occur with the dateGreaterThan validator.
        this.$nextTick(() => (this.$refs.validToDate as any).validate());
      }
    },
    validToDate: {
      get(): any {
        return this.model.validToDate;
      },
      set(value: Date) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_VALID_TO_DATE),
          value
        );
      }
    },
    legal: {
      get(): string {
        return this.model.legal;
      },
      set(value: string) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_LEGAL),
          value
        );
      }
    },
    legalHtml: {
      get(): string {
        return this.model.legalHtml;
      },
      set(value: string) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_LEGAL_HTML),
          value
        );
      }
    },
    benefits: {
      get(): string {
        return this.model.benefits;
      },
      set(value: string) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_BENEFITS),
          value
        );
      }
    },
    benefitsHtml: {
      get(): string {
        return this.model.benefitsHtml;
      },
      set(value: string) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_BENEFITS_HTML),
          value
        );
      }
    },
    earnsLoyaltyPoints: {
      get(): boolean {
        return this.model.earnsLoyaltyPoints;
      },
      set(value: boolean) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_EARNS_LOYALTY_POINTS),
          value
        );
      }
    },
    isDiscountOffPump: {
      get(): boolean {
        return this.model.isDiscountOffPump;
      },
      set(value: boolean) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_DISCOUNTS_OFF_PUMP),
          value
        );
      }
    },
    skipCreditCheck: {
      get(): boolean {
        return this.model.skipCreditCheck;
      },
      set(value: boolean) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_SKIP_CREDIT_CHECK),
          value
        );
      }
    },
    discounts: {
      get(): any {
        return this.model.discounts;
      },
      set(value: any) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_DISCOUNTS),
          value
        );
      }
    },
    applicationType: {
      get(): any {
        return this.model.applicationType;
      },
      set(value: string) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_APPLICATION_TYPE),
          value
        );
      }
    },
    customerType: {
      get(): any {
        return this.model.customerType;
      },
      set(value: string) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_CUSTOMER_TYPE),
          value
        );
      }
    },
    classOfBuyer: {
      get(): any {
        return this.model.classOfBuyer;
      },
      set(value: string) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_CLASS_OF_BUYER),
          value
        );
      }
    },
    pricingProfile: {
      get(): any {
        return this.model.pricingProfile;
      },
      set(value: string) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_PRICING_PROFILE),
          value
        );
      }
    },
    rebateProfile: {
      get(): any {
        return this.model.rebateProfile;
      },
      set(value: string[]) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_REBATE_PROFILE),
          value
        );
      }
    },
    paymentTerm: {
      get(): any {
        return this.model.paymentTerm;
      },
      set(value: string) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_PAYMENT_TERM),
          value
        );
      }
    },
    paymentMethod: {
      get(): string {
        return this.model.paymentMethod;
      },
      set(value: string) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_PAYMENT_METHOD),
          value
        );
      }
    },
    accountFeeProfile: {
      get(): any {
        return this.model.accountFeeProfile;
      },
      set(value: string) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_ACCOUNT_FEE_PROFILE),
          value
        );
      }
    },
    cardFeeProfile: {
      get(): any {
        return this.model.cardFeeProfile;
      },
      set(value: string) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_CARD_FEE_PROFILE),
          value
        );
      }
    },
    salesRepresentative: {
      get(): any {
        return this.model.salesRepresentative;
      },
      set(value: string) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_SALES_REPRESENTATIVE),
          value
        );
      }
    },
    collectionManager: {
      get(): any {
        return this.model.collectionManager;
      },
      set(value: string) {
        this.$store.commit(
          PROMO_CODES.NS(PROMO_CODES.MUTATIONS.SET_COLLECTION_MANAGER),
          value
        );
      }
    }
  },
  methods: {
    async fetchData() {
      this.$store.dispatch(
        REFERENCE_DATA.NS(REFERENCE_DATA.ACTIONS.FETCH_REFERENCE_DATA)
      );

      if (this.id) {
        await this.$store.dispatch(
          PROMO_CODES.NS(PROMO_CODES.ACTIONS.LOAD_PROMO_OFFER_BY_OFFER_ID),
          this.id
        );
      }

      this.originalValidFromDate =
        (this.model && this.model.validFromDate) || null;
    },
    async onSubmit() {
      if (this.id && this.id !== "") {
        await this.$store.dispatch(
          PROMO_CODES.NS(PROMO_CODES.ACTIONS.UPDATE_PROMO_OFFER),
          this.id
        );
      } else {
        const success = await this.$store.dispatch(
          PROMO_CODES.NS(PROMO_CODES.ACTIONS.CREATE_PROMO_OFFER)
        );

        if (success) this.$router.push({ name: ROUTE_NAMES.PROMO_CODES });
      }
    }
  },
  validations() {
    return {
      promoCode: {
        required,
        maxLength: maxLength(20)
      },
      validFromDate: {
        required,
        date
      },
      validToDate: {
        greaterThan: dateGreaterThan("validFromDate"),
        date
      },
      legalLength: {
        maxLength: maxValue(EDITOR_MAX_LENGTH)
      },
      benefitsLength: {
        maxLength: maxValue(EDITOR_MAX_LENGTH)
      },
      discounts: {
        required: requiredIf(vm => vm.isDiscountOffPump)
      },
      applicationType: {
        required
      },
      customerType: {
        required
      },
      classOfBuyer: {
        required
      },
      pricingProfile: {
        required
      },
      rebateProfile: {
        required
      },
      paymentTerm: {
        required
      },
      paymentMethod: {
        required
      },
      salesRepresentative: {
        required
      },
      collectionManager: {
        required
      }
    };
  }
});
